import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { X, Send, User, Loader2, Sparkles, ArrowRight, MapPin, History, Plus, Trash2, ChevronLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_CONVERSATIONS = 3;
const STORAGE_KEY = 'ikonic_chat_history';

const AIChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [suggestedProperties, setSuggestedProperties] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const welcomeMessage = {
    role: 'assistant',
    content: '¡Hola! 👋 Soy Iko, tu asistente inteligente de Ikonic Deals. Cuéntame qué tipo de propiedad buscas y te ayudo a encontrar opciones perfectas para ti.'
  };

  // Load conversations from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setConversations(parsed.slice(0, MAX_CONVERSATIONS));
      } catch (e) {
        console.error('Error loading chat history:', e);
      }
    }
  }, []);

  // Save conversations to localStorage
  const saveConversations = useCallback((convs) => {
    const toSave = convs.slice(0, MAX_CONVERSATIONS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    setConversations(toSave);
  }, []);

  // Start a new conversation
  const startNewConversation = useCallback(() => {
    const newId = `conv-${Date.now()}`;
    const newConv = {
      id: newId,
      title: 'Nueva conversación',
      messages: [welcomeMessage],
      createdAt: new Date().toISOString(),
      sessionId: null
    };
    
    // Add to beginning and limit to MAX_CONVERSATIONS
    const updated = [newConv, ...conversations].slice(0, MAX_CONVERSATIONS);
    saveConversations(updated);
    
    setCurrentConversationId(newId);
    setMessages([welcomeMessage]);
    setSessionId(null);
    setSuggestedProperties([]);
    setShowHistory(false);
  }, [conversations, saveConversations]);

  // Load an existing conversation
  const loadConversation = useCallback((conv) => {
    setCurrentConversationId(conv.id);
    setMessages(conv.messages);
    setSessionId(conv.sessionId);
    setSuggestedProperties([]);
    setShowHistory(false);
  }, []);

  // Delete a conversation
  const deleteConversation = useCallback((convId, e) => {
    e.stopPropagation();
    const updated = conversations.filter(c => c.id !== convId);
    saveConversations(updated);
    
    if (currentConversationId === convId) {
      if (updated.length > 0) {
        loadConversation(updated[0]);
      } else {
        startNewConversation();
      }
    }
  }, [conversations, currentConversationId, loadConversation, saveConversations, startNewConversation]);

  // Update current conversation in storage
  const updateCurrentConversation = useCallback((newMessages, newSessionId, newTitle) => {
    if (!currentConversationId) return;
    
    const updated = conversations.map(conv => {
      if (conv.id === currentConversationId) {
        return {
          ...conv,
          messages: newMessages,
          sessionId: newSessionId || conv.sessionId,
          title: newTitle || conv.title,
          updatedAt: new Date().toISOString()
        };
      }
      return conv;
    });
    
    saveConversations(updated);
  }, [conversations, currentConversationId, saveConversations]);

  // Initialize with a conversation when opening
  useEffect(() => {
    if (isOpen && !currentConversationId) {
      if (conversations.length > 0) {
        // Load most recent conversation
        loadConversation(conversations[0]);
      } else {
        // Start new conversation
        startNewConversation();
      }
    }
  }, [isOpen, currentConversationId, conversations, loadConversation, startNewConversation]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !showHistory) {
      inputRef.current?.focus();
    }
  }, [isOpen, showHistory]);

  // Extract property suggestions from AI response
  const extractPropertyLinks = (text) => {
    const regex = /\/propiedad\/([a-zA-Z0-9-]+)/g;
    const matches = [...text.matchAll(regex)];
    return matches.map(m => m[1]);
  };

  // Generate title from first user message
  const generateTitle = (message) => {
    const maxLength = 30;
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    
    const newMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);
    setIsLoading(true);

    // Update title if this is the first user message
    const isFirstUserMessage = messages.filter(m => m.role === 'user').length === 0;
    if (isFirstUserMessage) {
      updateCurrentConversation(newMessages, sessionId, generateTitle(userMessage));
    }

    try {
      const response = await fetch(`${API_URL}/api/ai/chat/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId,
          history: messages.slice(-10)
        })
      });

      if (!response.ok) {
        throw new Error('Error en la respuesta');
      }

      const data = await response.json();
      
      setSessionId(data.session_id);
      
      // Check if response contains property suggestions
      const propertyLinks = extractPropertyLinks(data.response);
      if (data.properties && data.properties.length > 0) {
        setSuggestedProperties(data.properties.slice(0, 3));
      } else if (propertyLinks.length > 0) {
        try {
          const propResponse = await fetch(`${API_URL}/api/properties?limit=3`);
          if (propResponse.ok) {
            const propData = await propResponse.json();
            setSuggestedProperties(propData.properties?.slice(0, 3) || []);
          }
        } catch (e) {
          console.error('Error fetching properties:', e);
        }
      }
      
      const finalMessages = [...newMessages, { role: 'assistant', content: data.response }];
      setMessages(finalMessages);
      updateCurrentConversation(finalMessages, data.session_id, isFirstUserMessage ? generateTitle(userMessage) : undefined);
      
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessages = [...newMessages, { 
        role: 'assistant', 
        content: 'Disculpa, tuve un problema al procesar tu mensaje. ¿Podrías intentarlo de nuevo?' 
      }];
      setMessages(errorMessages);
      updateCurrentConversation(errorMessages, sessionId);
    } finally {
      setIsLoading(false);
    }
  };

  const quickActions = [
    { label: '🏠 Casas en venta', message: 'Busco casas en venta en Guatemala' },
    { label: '🏢 Apartamentos', message: 'Muéstrame apartamentos disponibles' },
    { label: '💰 Menos de $200K', message: 'Propiedades económicas, menos de $200,000' },
    { label: '✨ Propiedades de lujo', message: 'Quiero ver propiedades de lujo' }
  ];

  // Render message with clickable property links
  const renderMessageContent = (content) => {
    const parts = content.split(/(\/propiedad\/[a-zA-Z0-9-]+)/g);
    
    return parts.map((part, index) => {
      if (part.startsWith('/propiedad/')) {
        return (
          <Link 
            key={index}
            to={part}
            className="text-[#D4AF37] hover:underline inline-flex items-center gap-1"
            onClick={() => setIsOpen(false)}
          >
            ver propiedad <ArrowRight size={12} />
          </Link>
        );
      }
      return part;
    });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Ahora';
    if (diff < 3600000) return `Hace ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Hace ${Math.floor(diff / 3600000)}h`;
    return date.toLocaleDateString('es-GT', { day: 'numeric', month: 'short' });
  };

  return (
    <>
      {/* Chat Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-gradient-to-r from-[#D4AF37] to-[#FFE182] rounded-full shadow-lg flex items-center justify-center hover:shadow-xl transition-shadow group"
            data-testid="chatbot-toggle"
          >
            <Sparkles className="w-7 h-7 text-black group-hover:scale-110 transition-transform" />
            <span className="absolute -top-2 -right-2 w-5 h-5 bg-green-500 rounded-full border-2 border-white animate-pulse" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] h-[550px] max-h-[calc(100vh-100px)] bg-[#0a0a0a] rounded-2xl shadow-2xl border border-white/10 flex flex-col overflow-hidden"
            data-testid="chatbot-window"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#D4AF37] to-[#B8963B] px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {showHistory && (
                  <button
                    onClick={() => setShowHistory(false)}
                    className="w-8 h-8 rounded-full bg-black/10 flex items-center justify-center hover:bg-black/20 transition-colors mr-1"
                  >
                    <ChevronLeft className="w-5 h-5 text-black" />
                  </button>
                )}
                <div className="w-10 h-10 bg-black/20 rounded-full flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-black" />
                </div>
                <div>
                  <h3 className="font-semibold text-black">
                    {showHistory ? 'Historial' : 'Iko - Asistente IA'}
                  </h3>
                  <p className="text-xs text-black/70">
                    {showHistory ? `${conversations.length} conversaciones` : 'Smart Property Match'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!showHistory && (
                  <>
                    <button
                      onClick={() => setShowHistory(true)}
                      className="w-8 h-8 rounded-full bg-black/10 flex items-center justify-center hover:bg-black/20 transition-colors"
                      title="Ver historial"
                      data-testid="chat-history-btn"
                    >
                      <History className="w-4 h-4 text-black" />
                    </button>
                    <button
                      onClick={startNewConversation}
                      className="w-8 h-8 rounded-full bg-black/10 flex items-center justify-center hover:bg-black/20 transition-colors"
                      title="Nueva conversación"
                      data-testid="new-chat-btn"
                    >
                      <Plus className="w-5 h-5 text-black" />
                    </button>
                  </>
                )}
                <button 
                  onClick={() => setIsOpen(false)}
                  className="w-8 h-8 rounded-full bg-black/10 flex items-center justify-center hover:bg-black/20 transition-colors"
                  data-testid="chatbot-close"
                >
                  <X className="w-5 h-5 text-black" />
                </button>
              </div>
            </div>

            {/* History View */}
            {showHistory ? (
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {conversations.length === 0 ? (
                  <div className="text-center py-12">
                    <History size={48} className="mx-auto text-white/20 mb-4" />
                    <p className="text-white/60">No hay conversaciones guardadas</p>
                    <button
                      onClick={startNewConversation}
                      className="mt-4 px-4 py-2 bg-[#D4AF37] text-black rounded-lg text-sm font-medium hover:bg-[#FFE182] transition-colors"
                    >
                      Iniciar conversación
                    </button>
                  </div>
                ) : (
                  conversations.map((conv) => (
                    <motion.div
                      key={conv.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className={`p-3 rounded-lg cursor-pointer transition-colors flex items-start gap-3 group ${
                        conv.id === currentConversationId
                          ? 'bg-[#D4AF37]/20 border border-[#D4AF37]/30'
                          : 'bg-white/5 hover:bg-white/10 border border-transparent'
                      }`}
                      onClick={() => loadConversation(conv)}
                      data-testid={`conversation-${conv.id}`}
                    >
                      <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Sparkles size={14} className="text-[#D4AF37]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">
                          {conv.title}
                        </p>
                        <p className="text-white/40 text-xs mt-0.5">
                          {formatDate(conv.updatedAt || conv.createdAt)} · {conv.messages.length} mensajes
                        </p>
                      </div>
                      <button
                        onClick={(e) => deleteConversation(conv.id, e)}
                        className="opacity-0 group-hover:opacity-100 w-7 h-7 rounded-full bg-red-500/20 flex items-center justify-center hover:bg-red-500/40 transition-all"
                        title="Eliminar"
                      >
                        <Trash2 size={14} className="text-red-400" />
                      </button>
                    </motion.div>
                  ))
                )}
              </div>
            ) : (
              <>
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.map((msg, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                    >
                      <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${
                        msg.role === 'user' ? 'bg-[#D4AF37]' : 'bg-white/10'
                      }`}>
                        {msg.role === 'user' ? (
                          <User className="w-4 h-4 text-black" />
                        ) : (
                          <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                        )}
                      </div>
                      <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl ${
                        msg.role === 'user' 
                          ? 'bg-[#D4AF37] text-black rounded-br-md' 
                          : 'bg-white/10 text-white rounded-bl-md'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">
                          {msg.role === 'assistant' ? renderMessageContent(msg.content) : msg.content}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                  
                  {/* Property Suggestions Cards */}
                  {suggestedProperties.length > 0 && !isLoading && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="space-y-2"
                    >
                      <p className="text-[#A3A3A3] text-xs px-2">Propiedades sugeridas:</p>
                      {suggestedProperties.map((prop, idx) => (
                        <Link
                          key={idx}
                          to={`/propiedad/${prop.slug || prop.id}`}
                          onClick={() => setIsOpen(false)}
                          className="flex items-center gap-3 p-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors border border-white/5 hover:border-[#D4AF37]/30"
                        >
                          {prop.images?.[0]?.url && (
                            <img 
                              src={prop.images[0].url} 
                              alt={prop.name}
                              className="w-12 h-12 object-cover rounded"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm truncate">{prop.name}</p>
                            <p className="text-[#A3A3A3] text-xs flex items-center gap-1">
                              <MapPin size={10} />
                              {prop.zone}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-[#D4AF37] text-sm font-mono">
                              ${prop.price_usd?.toLocaleString()}
                            </p>
                            <ArrowRight size={14} className="text-[#A3A3A3] ml-auto" />
                          </div>
                        </Link>
                      ))}
                    </motion.div>
                  )}
                  
                  {/* Loading indicator */}
                  {isLoading && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex gap-3"
                    >
                      <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                      </div>
                      <div className="bg-white/10 px-4 py-3 rounded-2xl rounded-bl-md">
                        <Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin" />
                      </div>
                    </motion.div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>

                {/* Quick Actions - only show at start */}
                {messages.length <= 2 && !isLoading && (
                  <div className="px-4 pb-2">
                    <p className="text-[#A3A3A3] text-xs mb-2">Sugerencias:</p>
                    <div className="flex flex-wrap gap-2">
                      {quickActions.map((action, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setInput(action.message);
                            inputRef.current?.focus();
                          }}
                          className="text-xs px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-white/80 hover:bg-white/10 hover:border-[#D4AF37]/50 transition-colors"
                          data-testid={`quick-action-${idx}`}
                        >
                          {action.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Input */}
                <form onSubmit={handleSubmit} className="p-4 border-t border-white/10">
                  <div className="flex gap-2">
                    <input
                      ref={inputRef}
                      type="text"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder="¿Qué propiedad buscas?"
                      disabled={isLoading}
                      className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]/50 transition-colors disabled:opacity-50"
                      data-testid="chatbot-input"
                    />
                    <button
                      type="submit"
                      disabled={!input.trim() || isLoading}
                      className="w-11 h-11 bg-[#D4AF37] rounded-xl flex items-center justify-center hover:bg-[#FFE182] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="chatbot-send"
                    >
                      <Send className="w-5 h-5 text-black" />
                    </button>
                  </div>
                </form>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIChatbot;
